import * as React from 'react'
import styled, { css } from 'styled-components'
import Icon from '../Icons/Icon'
import { Style } from '../../styles'
import { useTranslation } from 'react-i18next'
import Popover from '../Popover/Popover'
import ActionList from '../ActionList/ActionList'
import { NodeRendererProps } from 'react-arborist'
import { PublicLocale, Locale, NewsCenterTreeData, NewsCenterTreeModelType } from '../../types'
import MobilityHelper from '../../helpers/MobilityHelper'

const DragButton = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
  cursor: grab;
  opacity: 0;
  transition: opacity 100ms ease-in-out;

  svg {
    width: 10px;
    color: rgb(100, 100, 98);
  }
`

const Collapse = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  transition: transform 100ms ease-in-out;
  margin-left: 20px;
  margin-right: 20px;
  cursor: pointer;

  svg {
    width: 12px;
    height: 12px;
  }
`

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${Style.color.border};
  border-radius: 50%;
  transition: background-color 100ms ease-in-out;
  margin-right: 12px;
  cursor: pointer;

  &:hover {
    background: #e9eae6 !important;
  }

  img {
    width: 26px;
    height: 26px;
  }
`

const Container = styled.div<{ open?: boolean, type: NewsCenterTreeModelType, willReceiveDrop?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  transition: background-color 100ms ease-in-out;
  border-bottom: 1px solid ${Style.color.border};
  background: white;
  width: 100%;
  cursor: grab;

  &:last-child {
    border-bottom: 0;
  }

  
  &:hover {
    background: #f0f1ef;

    ${DragButton} {
      opacity: 1;
    }

    ${IconContainer} {
      background: white;
    }
  }

  ${props => props.willReceiveDrop && css`
    background: #f0f1ef;

    ${DragButton} {
      opacity: 1;
    }
  `}

  ${DragButton} {
    position: absolute;
    left: 8px;
  }

  ${props => !props.open && css`
    ${Collapse} {
      transform: rotate(-90deg);
    }
  `}

  ${props => props.type === NewsCenterTreeModelType.NEWS && css`
    ${IconContainer} {
      border-color: transparent;
      background-color: transparent !important;

      &:hover {
        background: transparent !important;
      }
      
      svg {
        width: 26px;
        height: 26px;
      }
    }
  `}

  ${props => props.type === NewsCenterTreeModelType.ADD_NEWS && css`
    cursor: pointer;

    ${IconContainer} {
      &:hover {
        background: initial !important;
      }
    }
    ${Collapse} {
      display: none;
    }

    ${DragButton} {
      display: none;
    }
  `}
`

const NameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: 16px;
  width: 100%;
  padding-left: 16px;
`

const Name = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`

const Metadata = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  max-width: 230px;
  cursor: pointer;

  @media screen and (max-width: 1510px) {
    max-width: 170px;
  }

  @media screen and (max-width: 1387px){
    max-width: 110px;
  }
`

const MetadataName = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`

const MetadataDescription = styled.div`
  color: rgb(100, 100, 98);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`

const Status = styled.div`
  display: flex;
  padding: 0px 16px;
  flex: 1;
  justify-content: center;
`

const Articles = styled.div`
  display: flex;
  padding: 0px 16px;
  flex: 1;
  justify-content: center;
`

const Action = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  max-width: 32px;
  padding: 0px 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`

const ActionButton = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	text-align: center;
	cursor: pointer;
	font-weight: 600;

	background: ${props => props.theme.buttonBackground};
	color: ${props => props.theme.buttonText};

	i {
		color: ${props => props.theme.buttonText};
	}
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 191.81px;
  margin-right: 10px;
`

type IProps = NodeRendererProps<NewsCenterTreeData> & {
  newsCenterId: string
  locale: PublicLocale
  onClick: (node: NewsCenterTreeData) => void
  onAddNewsClick: (node: NewsCenterTreeData) => void
  onEditClick: (node: NewsCenterTreeData) => void
  onDeleteClick: (node: NewsCenterTreeData) => void
}

const Item = (props: IProps) => {
  const {
    node,
    dragHandle,
    style,
    newsCenterId,
    locale,
    tree,
    onClick,
    onAddNewsClick,
    onEditClick,
    onDeleteClick,
  } = props
  const { t } = useTranslation()
  const [popoverActive, setPopoverActive] = React.useState(false)

  let type: NewsCenterTreeModelType = node.data.type
  let name = ''
  let description = ''
  let articlesCount = null

  switch (type) {
    case NewsCenterTreeModelType.NEWS:
      name = MobilityHelper.getTranslation((locale as unknown) as Locale, node.data.model.translations, 'title')
      description = MobilityHelper.getTranslation((locale as unknown) as Locale, node.data.model.translations, 'summary')
      break
    case NewsCenterTreeModelType.ADD_NEWS:
      description = t('Item::Add news')
      break
    default:
      throw new Error(`Invalid type: ${type} given`)
  }


  const onPopoverClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setPopoverActive(!popoverActive)
  }

  const onPopoverClose = () => {
    setPopoverActive(false)
  }

  const onItemClick = () => {
    if (type === NewsCenterTreeModelType.ADD_NEWS) {
      onAddNewsClick(node.parent.data)
      return
    } else {
      onClick(node.data)
    }
  }

  const onItemEditClick = () => {
    onEditClick(node.data)
  }

  const onItemDeleteClick = () => {
    onDeleteClick(node.data)
  }

  return (
    <Container
      open={node.isOpen}
      style={{ ...style }}
      type={type}
      willReceiveDrop={node.state.willReceiveDrop}
      onClick={onItemClick}
      ref={dragHandle}
    >
      <DragButton
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}>
        <Icon icon='grip-vertical' />
      </DragButton>

      <NameContainer>
        <Name>
          {type === NewsCenterTreeModelType.NEWS && <IconContainer>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path d="M34.5 32.5v-24a1 1 0 0 0-.29-.71l-7-7A1 1 0 0 0 26.5.5h-20a1 1 0 0 0-1 1v37a1 1 0 0 0 1 1h27a1 1 0 0 0 1-1v-3h-2v2h-21a4 4 0 0 1-4-4v-31h17v7a1 1 0 0 0 1 1h7v22zm-8-29.59l5.59 5.59H26.5z"></path><path d="M11.5 13.5h17v2h-17zm0 5h17v2h-17zm0 5h17v2h-17zm0 5h10v2h-10z"></path></svg>
          </IconContainer>}
          {type === NewsCenterTreeModelType.ADD_NEWS && <IconContainer>
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M14 7.15H8.85V2H7.15V7.15H2V8.85H7.15V14H8.85V8.85H14V7.15Z"></path></svg>
          </IconContainer>}

          <Metadata>
            {name?.length > 0 && <MetadataName>
              {name}
            </MetadataName>}

            <MetadataDescription>
              {description}
            </MetadataDescription>
          </Metadata>
        </Name>
      </NameContainer>

      <InfoContainer>

        <Status>
        </Status>

        <Articles>
          {articlesCount !== null && articlesCount}
        </Articles>

        <Action>
          {type !== NewsCenterTreeModelType.ADD_NEWS && <Popover
            active={popoverActive}
            activator={
              <ActionButton onClick={onPopoverClick}>
                <svg viewBox='0 0 20 20' focusable='false' aria-hidden='true'><path d='M6 10a2 2 0 1 1-4.001-.001A2 2 0 0 1 6 10zm6 0a2 2 0 1 1-4.001-.001A2 2 0 0 1 12 10zm6 0a2 2 0 1 1-4.001-.001A2 2 0 0 1 18 10z' fillRule='evenodd'></path></svg>
              </ActionButton>
            }
            onClose={onPopoverClose}
            placement='bottom-end'
            strategy='fixed'
          >
            <ActionList
              actions={[
                { key: 'edit', icon: 'edit-solid', content: t('Item::Edit'), onClick: onItemEditClick },
                { key: 'delete', icon: 'trash-alt-solid', content: t('Item::Delete'), destructive: true, onClick: onItemDeleteClick }
              ]}
              onClick={(_key: string) => {
                onPopoverClose()
              }}
              hasIndicator
            />
          </Popover>}
        </Action>
      </InfoContainer>
    </Container>
  )
}

export default Item